import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PanelControls } from '~/components/ui/PanelControls';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import { getRandomInt, InputAction } from '~/shared';
import cursorImage from '../assets/images/cursor.svg';
import {
	StyledContainer,
	StyledControlsContainer,
	StyledPercent,
	StyledSvg,
} from './CatchRing.styled';

const CIRCLE_CIRCUMFERENCE = 267.03; // Длина окружности для расчета stroke-dasharray
const INITIAL_PERCENT = 0; // Начальный процент прогресса
const PERCENT_INCREASE = 5; // Базовое увеличение процентов за одно попадание
const PERCENT_DECREASE_INTERVAL = 100; // Интервал уменьшения процентов при промахе (мс)
const BASE_SPEED = 2; // Базовая скорость вращения курсора (секунды на полный оборот)
const SPEED_RANGE = 0.5; // Диапазон случайного изменения скорости
const COMBO_SPEED_MULTIPLIER = 0.05; // Множитель увеличения скорости за каждое комбо
const COMBO_PERCENT_MULTIPLIER = 1.2; // Множитель увеличения процентов за комбо
const MAX_COMBO = 10; // Максимальное комбо для расчета бонусов
const CLOSE_ZONE_THRESHOLD = 30; // Порог близости полосок в градусах (игнорируем выход если полоски близко)
const PERCENT_ANIMATION_DURATION = 300; // Длительность анимации прибавления процентов (мс)

const CatchRing = () => {
	const actionName = useObservable(rage.catchRing.getActionName());
	const [length, setLength] = useState(
		() => CIRCLE_CIRCUMFERENCE - getRandomInt(40, 60),
	);
	const [initialRotate, setInitialRotate] = useState(() =>
		getRandomInt(0, 360),
	);
	const speed = useMemo(() => BASE_SPEED + _.random(0, SPEED_RANGE), []);
	const animationStartTime = useRef<number>(performance.now());
	const directionRef = useRef(1);
	const animationFrameRef = useRef<number>();
	const angleRef = useRef(0);
	const baseAngleRef = useRef(0);
	const cursorElementRef = useRef<SVGGElement>(null);
	const speedRef = useRef(speed);
	const percentRef = useRef(INITIAL_PERCENT);
	const percentTextRef = useRef<HTMLDivElement>(null);
	const percentTimerRef = useRef<NodeJS.Timeout>();
	const comboRef = useRef(0);
	const isInZoneRef = useRef(false);
	const previousZoneAngleRef = useRef<number | null>(null);
	const percentAnimationRef = useRef<number>();
	const targetPercentRef = useRef(INITIAL_PERCENT);
	const previousInitialRotateRef = useRef(initialRotate);
	const waitingForHitRef = useRef(true);
	const hitCooldownRef = useRef(0);
	const closeZoneExitCountRef = useRef(0);
	const justExitedCooldownRef = useRef(false);
	const gameStartedRef = useRef(false);
	const gameFinishedRef = useRef(false);

	useEffect(() => {
		const comboMultiplier = 1 + comboRef.current * COMBO_SPEED_MULTIPLIER;
		speedRef.current = speed * comboMultiplier;
	}, [speed]);

	useEffect(() => {
		animationStartTime.current = performance.now();
		baseAngleRef.current = 0;
		angleRef.current = 0;
		directionRef.current = 1;
		comboRef.current = 0;
		isInZoneRef.current = false;
		previousZoneAngleRef.current = null;
		previousInitialRotateRef.current = initialRotate;
		waitingForHitRef.current = true;
		closeZoneExitCountRef.current = 0;
		justExitedCooldownRef.current = false;
		gameStartedRef.current = false;
		gameFinishedRef.current = false;
	}, []);

	const updatePercentText = useCallback((value: number) => {
		if (percentTextRef.current) {
			percentTextRef.current.textContent = `${Math.round(value)}%`;
		}
	}, []);

	const animatePercentIncrease = useCallback(
		(from: number, to: number) => {
			if (percentAnimationRef.current) {
				cancelAnimationFrame(percentAnimationRef.current);
			}

			const startTime = performance.now();
			const startValue = from;
			const difference = to - from;

			const animate = (currentTime: number) => {
				const elapsed = currentTime - startTime;
				const progress = Math.min(elapsed / PERCENT_ANIMATION_DURATION, 1);

				const easedProgress = 1 - Math.pow(1 - progress, 3);
				const currentValue = startValue + difference * easedProgress;

				percentRef.current = currentValue;
				updatePercentText(currentValue);

				if (progress < 1) {
					percentAnimationRef.current = requestAnimationFrame(animate);
				} else {
					percentRef.current = to;
					updatePercentText(to);
					percentAnimationRef.current = undefined;
				}
			};

			percentAnimationRef.current = requestAnimationFrame(animate);
		},
		[updatePercentText],
	);

	const handleLose = useCallback(() => {
		percentRef.current = 0;
		updatePercentText(0);
		if (gameStartedRef.current) {
			rage.catchRing
				.catch(false)
				.catch((err) => rage.notifications.showException(err));
			rage.catchRing
				.exit()
				.catch((err) => rage.notifications.showException(err));
		}
	}, [updatePercentText]);

	const handleWin = useCallback(() => {
		rage.catchRing
			.catch(true)
			.catch((err) => rage.notifications.showException(err));
		rage.catchRing.exit().catch((err) => rage.notifications.showException(err));
	}, []);

	const startDecreasingPercentRef = useRef<() => void>();

	const startDecreasingPercent = useCallback(() => {
		if (percentTimerRef.current) {
			return;
		}

		const finalizeLose = () => {
			if (percentTimerRef.current) {
				clearInterval(percentTimerRef.current);
				percentTimerRef.current = undefined;
			}
			handleLose();
		};

		if (percentRef.current <= 0) {
			finalizeLose();
			return;
		}

		percentTimerRef.current = setInterval(() => {
			if (percentRef.current <= 0) {
				finalizeLose();
				return;
			}

			percentRef.current -= 1;
			updatePercentText(percentRef.current);
		}, PERCENT_DECREASE_INTERVAL);
	}, [handleLose, updatePercentText]);

	useEffect(() => {
		startDecreasingPercentRef.current = startDecreasingPercent;
	}, [startDecreasingPercent]);

	const stopDecreasingPercent = useCallback(() => {
		if (percentTimerRef.current) {
			clearInterval(percentTimerRef.current);
			percentTimerRef.current = undefined;
		}
	}, []);

	useEffect(() => {
		percentRef.current = INITIAL_PERCENT;
		updatePercentText(INITIAL_PERCENT);

		return () => {
			stopDecreasingPercent();
		};
	}, [updatePercentText, stopDecreasingPercent]);

	const checkZoneIntersection = useCallback(
		(cursorAngle: number, zoneStartAngle: number, zoneSize: number) => {
			const relativeAngle = (cursorAngle - zoneStartAngle + 360) % 360;
			return relativeAngle <= zoneSize;
		},
		[],
	);

	const lengthRef = useRef(length);
	const initialRotateRef = useRef(initialRotate);

	useEffect(() => {
		lengthRef.current = length;
		initialRotateRef.current = initialRotate;
	}, [length, initialRotate]);

	useEffect(() => {
		const animate = () => {
			if (gameFinishedRef.current) {
				return;
			}

			const now = performance.now();
			const elapsedTime = now - animationStartTime.current;
			const degreesPerSecond = 360 / speedRef.current;
			const angleDelta =
				(elapsedTime / 1000) * degreesPerSecond * directionRef.current;

			let angle = baseAngleRef.current + angleDelta;

			angle = ((angle % 360) + 360) % 360;

			angleRef.current = angle;

			const element = cursorElementRef.current;
			if (element) {
				element.setAttribute('transform', `rotate(${angle} 75 75)`);
			}

			const currentLength = lengthRef.current;
			const currentInitialRotate = initialRotateRef.current;
			const arcLength = CIRCLE_CIRCUMFERENCE - currentLength;
			const arcAngleDegrees = (arcLength / CIRCLE_CIRCUMFERENCE) * 360;
			const CAP_TOLERANCE = 6;
			const geomStartAngle = (90 + currentInitialRotate) % 360;
			let hitStartAngle = (geomStartAngle - CAP_TOLERANCE) % 360;
			if (hitStartAngle < 0) hitStartAngle += 360;
			const totalHitZoneDegrees = arcAngleDegrees + 2 * CAP_TOLERANCE;

			const isCurrentlyInZone = checkZoneIntersection(
				angle,
				hitStartAngle,
				totalHitZoneDegrees,
			);

			if (hitCooldownRef.current > 0) {
				hitCooldownRef.current -= 1;
				isInZoneRef.current = isCurrentlyInZone;

				if (hitCooldownRef.current === 0) {
					waitingForHitRef.current = true;
					justExitedCooldownRef.current = true;
				}

				animationFrameRef.current = requestAnimationFrame(animate);
				return;
			}

			if (justExitedCooldownRef.current) {
				justExitedCooldownRef.current = false;
				isInZoneRef.current = isCurrentlyInZone;
				animationFrameRef.current = requestAnimationFrame(animate);
				return;
			}

			if (
				isInZoneRef.current &&
				!isCurrentlyInZone &&
				waitingForHitRef.current
			) {
				const previousZoneAngle = previousZoneAngleRef.current;

				let shouldStartDecreasing = true;

				if (previousZoneAngle !== null) {
					const angleDiff = Math.abs(
						((hitStartAngle - previousZoneAngle + 180) % 360) - 180,
					);
					const minDiff = Math.min(angleDiff, 360 - angleDiff);

					if (minDiff < CLOSE_ZONE_THRESHOLD) {
						closeZoneExitCountRef.current += 1;
						if (closeZoneExitCountRef.current === 1) {
							shouldStartDecreasing = false;
						}
					} else {
						closeZoneExitCountRef.current = 0;
					}
				}

				if (shouldStartDecreasing) {
					startDecreasingPercentRef.current?.();
				}
			}

			isInZoneRef.current = isCurrentlyInZone;

			if (Math.abs(baseAngleRef.current) >= 360) {
				baseAngleRef.current = baseAngleRef.current % 360;
				if (baseAngleRef.current < 0) baseAngleRef.current += 360;
			}

			animationFrameRef.current = requestAnimationFrame(animate);
		};

		animationFrameRef.current = requestAnimationFrame(animate);

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, [checkZoneIntersection]);

	useEffect(() => {
		return () => {
			if (percentAnimationRef.current) {
				cancelAnimationFrame(percentAnimationRef.current);
			}
		};
	}, []);

	const handleCatch = useCallback(() => {
		if (gameFinishedRef.current) {
			return;
		}

		gameStartedRef.current = true;

		const currentAngle = angleRef.current;
		const newDirection = -directionRef.current;

		baseAngleRef.current = currentAngle;
		animationStartTime.current = performance.now();

		directionRef.current = newDirection;

		let cursorAngle = currentAngle % 360;
		if (cursorAngle < 0) cursorAngle += 360;

		const arcLength = CIRCLE_CIRCUMFERENCE - length;
		const arcAngleDegrees = (arcLength / CIRCLE_CIRCUMFERENCE) * 360;

		const CAP_TOLERANCE = 6;

		const geomStartAngle = (90 + initialRotate) % 360;

		let hitStartAngle = (geomStartAngle - CAP_TOLERANCE) % 360;
		if (hitStartAngle < 0) hitStartAngle += 360;

		const totalHitZoneDegrees = arcAngleDegrees + 2 * CAP_TOLERANCE;

		const relativeCursorAngle = (cursorAngle - hitStartAngle + 360) % 360;
		const isHit = relativeCursorAngle <= totalHitZoneDegrees;

		if (isHit) {
			stopDecreasingPercent();

			isInZoneRef.current = false;
			hitCooldownRef.current = 30;
			waitingForHitRef.current = false;
			closeZoneExitCountRef.current = 0;

			comboRef.current += 1;
			const comboMultiplier = Math.min(
				1 + (comboRef.current - 1) * (COMBO_PERCENT_MULTIPLIER - 1),
				1 + (MAX_COMBO - 1) * (COMBO_PERCENT_MULTIPLIER - 1),
			);
			const percentIncrease = PERCENT_INCREASE * comboMultiplier;

			const speedMultiplier = 1 + comboRef.current * COMBO_SPEED_MULTIPLIER;
			speedRef.current = speed / speedMultiplier;

			const currentPercent = percentRef.current;
			const newPercent = Math.min(100, currentPercent + percentIncrease);
			targetPercentRef.current = newPercent;
			animatePercentIncrease(currentPercent, newPercent);

			previousZoneAngleRef.current = hitStartAngle;

			if (newPercent >= 100) {
				gameFinishedRef.current = true;
				stopDecreasingPercent();
				if (animationFrameRef.current) {
					cancelAnimationFrame(animationFrameRef.current);
					animationFrameRef.current = undefined;
				}
				setTimeout(() => {
					handleWin();
				}, PERCENT_ANIMATION_DURATION);
				return;
			}

			const currentLength = length;
			const visibleLength = CIRCLE_CIRCUMFERENCE - currentLength;
			const minVisibleLength = 20;

			if (visibleLength > minVisibleLength) {
				const decreasePercent = getRandomInt(3, 6) / 100;
				const decreaseAmount = visibleLength * decreasePercent;
				const newVisibleLength = Math.max(
					minVisibleLength,
					visibleLength - decreaseAmount,
				);
				const newLength = CIRCLE_CIRCUMFERENCE - newVisibleLength;

				setLength(newLength);
			}

			const newRotate = getRandomInt(0, 360);
			previousInitialRotateRef.current = initialRotate;
			setInitialRotate(newRotate);
		} else {
			comboRef.current = 0;
			speedRef.current = speed;
			startDecreasingPercent();
		}
	}, [
		length,
		initialRotate,
		stopDecreasingPercent,
		startDecreasingPercent,
		updatePercentText,
		handleWin,
		animatePercentIncrease,
		speed,
	]);

	const restartGame = useCallback(() => {
		gameFinishedRef.current = false;
		percentRef.current = INITIAL_PERCENT;
		updatePercentText(INITIAL_PERCENT);
		comboRef.current = 0;
		isInZoneRef.current = false;
		previousZoneAngleRef.current = null;
		waitingForHitRef.current = true;
		closeZoneExitCountRef.current = 0;
		justExitedCooldownRef.current = false;
		gameStartedRef.current = false;
		hitCooldownRef.current = 0;
		targetPercentRef.current = INITIAL_PERCENT;

		stopDecreasingPercent();

		if (percentAnimationRef.current) {
			cancelAnimationFrame(percentAnimationRef.current);
			percentAnimationRef.current = undefined;
		}

		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = undefined;
		}

		const newLength = CIRCLE_CIRCUMFERENCE - getRandomInt(40, 60);
		const newInitialRotate = getRandomInt(0, 360);

		setLength(newLength);
		setInitialRotate(newInitialRotate);
		previousInitialRotateRef.current = newInitialRotate;

		animationStartTime.current = performance.now();
		baseAngleRef.current = 0;
		angleRef.current = 0;
		directionRef.current = 1;

		const comboMultiplier = 1 + comboRef.current * COMBO_SPEED_MULTIPLIER;
		speedRef.current = speed * comboMultiplier;

		const animate = () => {
			if (gameFinishedRef.current) {
				return;
			}

			const now = performance.now();
			const elapsedTime = now - animationStartTime.current;
			const degreesPerSecond = 360 / speedRef.current;
			const angleDelta =
				(elapsedTime / 1000) * degreesPerSecond * directionRef.current;

			let angle = baseAngleRef.current + angleDelta;

			angle = ((angle % 360) + 360) % 360;

			angleRef.current = angle;

			const element = cursorElementRef.current;
			if (element) {
				element.setAttribute('transform', `rotate(${angle} 75 75)`);
			}

			const currentLength = lengthRef.current;
			const currentInitialRotate = initialRotateRef.current;
			const arcLength = CIRCLE_CIRCUMFERENCE - currentLength;
			const arcAngleDegrees = (arcLength / CIRCLE_CIRCUMFERENCE) * 360;
			const CAP_TOLERANCE = 6;
			const geomStartAngle = (90 + currentInitialRotate) % 360;
			let hitStartAngle = (geomStartAngle - CAP_TOLERANCE) % 360;
			if (hitStartAngle < 0) hitStartAngle += 360;
			const totalHitZoneDegrees = arcAngleDegrees + 2 * CAP_TOLERANCE;

			const isCurrentlyInZone = checkZoneIntersection(
				angle,
				hitStartAngle,
				totalHitZoneDegrees,
			);

			if (hitCooldownRef.current > 0) {
				hitCooldownRef.current -= 1;
				isInZoneRef.current = isCurrentlyInZone;

				if (hitCooldownRef.current === 0) {
					waitingForHitRef.current = true;
					justExitedCooldownRef.current = true;
				}

				animationFrameRef.current = requestAnimationFrame(animate);
				return;
			}

			if (justExitedCooldownRef.current) {
				justExitedCooldownRef.current = false;
				isInZoneRef.current = isCurrentlyInZone;
				animationFrameRef.current = requestAnimationFrame(animate);
				return;
			}

			if (
				isInZoneRef.current &&
				!isCurrentlyInZone &&
				waitingForHitRef.current
			) {
				const previousZoneAngle = previousZoneAngleRef.current;

				let shouldStartDecreasing = true;

				if (previousZoneAngle !== null) {
					const angleDiff = Math.abs(
						((hitStartAngle - previousZoneAngle + 180) % 360) - 180,
					);
					const minDiff = Math.min(angleDiff, 360 - angleDiff);

					if (minDiff < CLOSE_ZONE_THRESHOLD) {
						closeZoneExitCountRef.current += 1;
						if (closeZoneExitCountRef.current === 1) {
							shouldStartDecreasing = false;
						}
					} else {
						closeZoneExitCountRef.current = 0;
					}
				}

				if (shouldStartDecreasing) {
					startDecreasingPercentRef.current?.();
				}
			}

			isInZoneRef.current = isCurrentlyInZone;

			if (Math.abs(baseAngleRef.current) >= 360) {
				baseAngleRef.current = baseAngleRef.current % 360;
				if (baseAngleRef.current < 0) baseAngleRef.current += 360;
			}

			animationFrameRef.current = requestAnimationFrame(animate);
		};

		animationFrameRef.current = requestAnimationFrame(animate);
	}, [updatePercentText, stopDecreasingPercent, checkZoneIntersection, speed]);

	const controls = [
		[
			{
				text: actionName,
				keys: ['Space'],
			},
		],
		[
			{
				text: 'Завершить действие',
				keys: ['Esc'],
				onClick: () =>
					rage.catchRing
						.exit()
						.catch((err) => rage.notifications.showException(err)),
			},
		],
	];

	useEffect(() => {
		if (rage.config.isStub()) {
			const handleKeyDown = (event: KeyboardEvent) => {
				if (event.key === 'r') {
					restartGame();
				}
			};

			window.addEventListener('keydown', handleKeyDown);

			return () => {
				window.removeEventListener('keydown', handleKeyDown);
			};
		}
	}, []);

	useInputAction(InputAction.CatchRingCatch, handleCatch);
	useInputAction(InputAction.CommonMenuClose, () => rage.catchRing.exit());

	return (
		<StyledContainer>
			<StyledSvg width="150" height="150">
				<circle
					cx="75"
					cy="75"
					r="42.5"
					fill="none"
					stroke="rgba(0, 0, 0, 0.5)"
					strokeWidth="15"
				/>
				<circle
					cx="75"
					cy="75"
					r="42.5"
					fill="none"
					stroke="#00DB1A"
					strokeWidth="9"
					strokeDasharray={267.03}
					strokeDashoffset={length}
					strokeLinecap="round"
					style={{
						transformOrigin: 'center',
						transform: `rotate(${initialRotate}deg)`,
					}}
				/>
				<g ref={cursorElementRef}>
					<image href={cursorImage} x="70" y="12" width="10" height="41" />
				</g>
			</StyledSvg>
			<StyledPercent>
				<Text ref={percentTextRef}>0%</Text>
			</StyledPercent>
			<StyledControlsContainer>
				{controls.map((control, index) => (
					<PanelControls key={index} controls={control} align={'right'} />
				))}
			</StyledControlsContainer>
		</StyledContainer>
	);
};

export { CatchRing };
